const Vue = require('vue')
const Quill = require('quill')
const Delta = require('quill-delta')
const { client, vueChaos } = require('sci/helpers')
const R = require('ramda')

const beautifyHtml = require('js-beautify/js/lib/beautify-html').html_beautify

const Resizer = function (quill, options = {}) {
    // save the quill reference and options
    this.quill = quill
    this.options = options
    // bind handlers to this instance
    this.handleClick = this.handleClick.bind(this)
    this.handleMousedown = this.handleMousedown.bind(this)
    this.handleMouseup = this.handleMouseup.bind(this)
    this.handleDrag = this.handleDrag.bind(this)
    this.checkImage = this.checkImage.bind(this)
    // track resize handles
    this.boxes = []
    // disable native image resizing on firefox
    document.execCommand('enableObjectResizing', false, 'false')
    // respond to clicks inside the editor
    this.quill.root.addEventListener('click', this.handleClick, false)
}

Resizer.prototype.handleClick = function (evt) {
    if (evt.target && evt.target.tagName && evt.target.tagName.toUpperCase() === 'IMG') {
        if (this.img === evt.target) {
            // we are already focused on this image
            return
        }
        if (this.img) {
            // we were just focused on another image
            this.hide()
        }
        // clicked on an image inside the editor
        this.show(evt.target)
    } else if (this.img) {
        // clicked on a non image
        this.hide()
    }
}

Resizer.prototype.show = function (img) {
    // keep track of this img element
    this.img = img
    this.showResizers()
    this.showSizeDisplay()
    // position the resize handles at the corners
    const rect = this.img.getBoundingClientRect()
    this.positionBoxes(rect)
    this.positionSizeDisplay(rect)
}

Resizer.prototype.hide = function () {
    this.hideResizers()
    this.hideSizeDisplay()
    this.img = undefined
}

Resizer.prototype.showResizers = function () {
    // prevent spurious text selection
    this.setUserSelect('none')
    // add 4 resize handles
    this.addBox('nwse-resize') // top left
    this.addBox('nesw-resize') // top right
    this.addBox('nwse-resize') // bottom right
    this.addBox('nesw-resize') // bottom left
    // listen for the image being deleted or moved
    document.addEventListener('keyup', this.checkImage, true)
    this.quill.root.addEventListener('input', this.checkImage, true)
}

Resizer.prototype.hideResizers = function () {
    // stop listening for image deletion or movement
    document.removeEventListener('keyup', this.checkImage)
    this.quill.root.removeEventListener('input', this.checkImage)
    // reset user-select
    this.setUserSelect('')
    this.setCursor('')
    // remove boxes
    this.boxes.forEach(box => document.body.removeChild(box))
    // release memory
    this.dragBox = undefined
    this.dragStartX = undefined
    this.preDragWidth = undefined
    this.boxes = []
}

Resizer.prototype.addBox = function (cursor) {
    // create div element for resize handle
    const box = document.createElement('div')
    // apply styles
    const styles = {
        position: 'absolute',
        height: '12px',
        width: '12px',
        backgroundColor: 'white',
        border: '1px solid #777',
        boxSizing: 'border-box',
        opacity: '0.80',
        cursor: cursor
    }
    this.extend(box.style, styles, this.options.handleStyles || {})
    // listen for mousedown on each box
    box.addEventListener('mousedown', this.handleMousedown, false)
    // add drag handle to document
    document.body.appendChild(box)
    // keep track of drag handle
    this.boxes.push(box)
}

Resizer.prototype.extend = function (destination, ...sources) {
    sources.forEach(source => {
        for (const prop in source) {
            if (source.hasOwnProperty(prop)) {
                destination[prop] = source[prop]
            }
        }
    })
    return destination
}

Resizer.prototype.positionBoxes = function positionBoxes (rect) {
    // set the top and left for each drag handle
    [
        { left: rect.left - 6, top: rect.top - 6 },               // top left
        { left: rect.left + rect.width - 6, top: rect.top - 6 },               // top right
        { left: rect.left + rect.width - 6, top: rect.top + rect.height - 6 }, // bottom right
        { left: rect.left - 6, top: rect.top + rect.height - 6 } // bottom left
    ].forEach((pos, idx) => {
        this.extend(this.boxes[idx].style, {
            top: Math.round(pos.top + window.pageYOffset) + 'px',
            left: Math.round(pos.left + window.pageXOffset) + 'px'
        })
    })
}

Resizer.prototype.handleMousedown = function (evt) {
    // note which box
    this.dragBox = evt.target
    // note starting mousedown position
    this.dragStartX = evt.clientX
    // store the width before the drag
    this.preDragWidth = this.img.width || this.img.naturalWidth
    // set the proper cursor everywhere
    this.setCursor(this.dragBox.style.cursor)
    // listen for movement and mouseup
    document.addEventListener('mousemove', this.handleDrag, false)
    document.addEventListener('mouseup', this.handleMouseup, false)
}

Resizer.prototype.handleMouseup = function () {
    // reset cursor everywhere
    this.setCursor('')
    // stop listening for movement and mouseup
    document.removeEventListener('mousemove', this.handleDrag)
    document.removeEventListener('mouseup', this.handleMouseup)
}

Resizer.prototype.handleDrag = function (evt) {
    if (!this.img) {
        // image not set yet
        return
    }

    // update image size
    if (this.dragBox === this.boxes[0] || this.dragBox === this.boxes[3]) {
        // left-side resize handler; draging right shrinks image
        this.img.width = Math.round(this.preDragWidth - evt.clientX - this.dragStartX)
    } else {
        // right-side resize handler; draging right enlarges image
        this.img.width = Math.round(this.preDragWidth + evt.clientX - this.dragStartX)
    }

    // reposition the drag handles around the image
    const rect = this.img.getBoundingClientRect()
    this.positionBoxes(rect)
    this.positionSizeDisplay(rect)
}

Resizer.prototype.setUserSelect = function (value) {
    [
        'userSelect',
        'mozUserSelect',
        'webkitUserSelect',
        'msUserSelect'
    ].forEach(prop => {
        // set on contenteditable element and <html>
        this.quill.root.style[prop] = value
        document.documentElement.style[prop] = value
    })
}

Resizer.prototype.setCursor = function (value) {
    [
        document.body,
        this.img,
        this.quill.root
    ].forEach(el => {
        el.style.cursor = value
    })
}

Resizer.prototype.checkImage = function () {
    if (this.img) {
        this.hide()
    }
}
Resizer.prototype.showSizeDisplay = function () {
    if (!this.options.displaySize) {
        return
    }
    this.display = document.createElement('div')
    // apply styles
    const styles = {
        position: 'absolute',
        font: '12px/1.0 Arial, Helvetica, sans-serif',
        padding: '4px 8px',
        textAlign: 'center',
        backgroundColor: 'white',
        color: '#333',
        border: '1px solid #777',
        boxSizing: 'border-box',
        opacity: '0.80',
        cursor: 'default'
    }
    this.extend(this.display.style, styles, this.options.displayStyles || {})
    document.body.appendChild(this.display)
}

Resizer.prototype.hideSizeDisplay = function () {
    document.body.removeChild(this.display)
    this.display = undefined
}

Resizer.prototype.positionSizeDisplay = function (rect) {
    if (!this.display || !this.img) {
        return
    }
    const size = this.getCurrentSize()
    this.display.innerHTML = size.join(' &times; ')
    if (size[0] > 120 && size[1] > 30) {
        // position on top of image
        const dispRect = this.display.getBoundingClientRect()
        this.extend(this.display.style, {
            left: Math.round(rect.left + rect.width + window.pageXOffset - dispRect.width - 8) + 'px',
            top: Math.round(rect.top + rect.height + window.pageYOffset - dispRect.height - 8) + 'px'
        })
    } else {
        // position off bottom right
        this.extend(this.display.style, {
            left: Math.round(rect.left + rect.width + window.pageXOffset + 8) + 'px',
            top: Math.round(rect.top + rect.height + window.pageYOffset + 8) + 'px'
        })
    }
}
Resizer.prototype.getCurrentSize = function () {
    return [
        this.img.width,
        Math.round(this.img.width / this.img.naturalWidth * this.img.naturalHeight)
    ]
}

Quill.register('modules/resizer', Resizer)

Vue.component('form-editor', {
    props: {
        type: {
            type: String,
            default: 'default'
        },
        label: {
            type: String
        },
        placeholder: {
            type: String
        },
        height: {
            default: 200,
            type: Number
        },
        rows: {
            type: String
        },
        cols: {
            type: String
        },
        model: {
            twoWay: true,
            default: null
        },
        allowLinks: {
            type: Boolean,
            default: true
        },
        allowImages: {
            type: Boolean,
            default: true
        },
        formGroupClasses: {
            default: null
        },
        autofocus: {
            type: Boolean,
            default: true
        }
    },
    methods: {
        holderClass () {
            return this.iconClass ? 'input-icon' : null
        }
    },
    data () {
        return {
            fieldName: '',
            actualHeight: '',
            delta: null,
            content: null
        }
    },
    watch: {
        model (newValue) {
            if (newValue !== this.content) {
                this.content = newValue

                this.quill.setContents(
                    this.quill.clipboard.convert(newValue || '')
                )
            }
        },
        content (newValue) {
            if (newValue !== this.model) {
                this.model = newValue
            }
        }
    },
    ready () {
        this.actualHeight = this.height + 'px'
        this.fieldName = this._props.model.raw
        this.content = this.model

        if (!this.placeholder) {
            this.placeholder = this.label
        }

        this.quill = new Quill(this.$els.editor, {
            placeholder: this.placeholder,
            theme: this.type === 'bubble' ? 'bubble' : 'snow',
            modules: {
                clipboard: {},
                toolbar: {
                    container: this.$els.toolbar,
                    handlers: {
                        'show-html': () => {
                            this.showHtml()
                        },
                        image: function (image, callback) {
                            let fileInput = this.container.querySelector('input.ql-image[type=file]')
                            if (fileInput === null) {
                                fileInput = document.createElement('input')
                                fileInput.setAttribute('type', 'file')
                                fileInput.setAttribute('accept', 'image/png, image/gif, image/jpeg, image/bmp, image/x-icon, image/svg+xml')
                                fileInput.classList.add('ql-image')

                                fileInput.addEventListener('change', () => {
                                    if (fileInput.files === null || fileInput.files[0] === null) {
                                        return
                                    }
                                    const file = fileInput.files[0]
                                    const formData = new FormData()

                                    formData.append('file', file)

                                    client
                                        .silent()
                                        .post('Enviando Imagem...', '/utils/images/upload', formData)
                                        .then(vueChaos())
                                        .then((result) => {
                                            const range = this.quill.getSelection(true)
                                            this.quill.updateContents(new Delta()
                                                .retain(range.index)
                                                .delete(range.length)
                                                .insert({ image: result.thumbnails.kxlarge })
                                                , 'user')
                                            fileInput.value = ''
                                        })
                                })

                                this.container.appendChild(fileInput)
                            }
                            fileInput.click()
                        }
                    }
                },
                resizer: {
                    displaySize: true
                }
                // toolbar: [
                //     [ { 'font': [] }, { 'size': [] } ],
                //     [ 'bold', 'italic', 'underline', 'strike' ],
                //     [ { 'color': [] }, { 'background': [] } ],
                //     [ { 'script': 'super' }, { 'script': 'sub' } ],
                //     [ { 'header': '1' }, { 'header': '2' }, 'blockquote', 'code-block' ],
                //     [ { 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' } ],
                //     [ 'direction', { 'align': [] } ],
                //     [ 'link', 'image', 'video' ],
                //     [ 'clean' ]
                // ],
            },
            bounds: this.$els.holder
        })

        // this.quill.pasteHTML(this.model || '')
        this.quill.setContents(
            this.quill.clipboard.convert(this.content) || ''
        )

        this.quill.on('text-change', (delta, oldDelta, source) => {
            this.delta = this.quill.getContents()

            if (source === 'user') {
                this.content = this.$els.editor.querySelector('.ql-editor').innerHTML
            }
        })

        // https://github.com/quilljs/quill/issues/110#issuecomment-43692304
        const keyboard = this.quill.getModule('keyboard')
        delete keyboard.bindings[9]

        this.$nextTick(() => {
            if (this.autofocus) {
                this.quill.focus()
            }
        })
    },
    template: `
        <div class="form-group" :class="formGroupClasses">
            <label v-if="label">{{ label }}</label>
            <div v-el:editor-holder style="position: relative">
                <div v-el:toolbar>
                    <!--
                    <span class="ql-formats">
                        <select class="ql-size bs-tooltip" title="Tamanho da fonte">
                            <option value="small">Pequeno</option>
                            <option selected>Normal</option>
                            <option value="large">Grande</option>
                            <option value="huge">Muito grande</option>
                        </select>
                    </span>
                    -->
                    <span class="ql-formats">
                        <select class="ql-color bs-tooltip" title="Cor da fonte"></select>
                        <select class="ql-background bs-tooltip" title="Cor do fundo"></select>
                    </span>
                    <span class="ql-formats">
                        <!-- Add a bold button -->
                        <button class="ql-bold bs-tooltip" title="Negrito"></button>
                        <button class="ql-italic bs-tooltip" title="Itálico"></button>
                        <button class="ql-underline bs-tooltip" title="Sublinhado"></button>
                        <button class="ql-strike bs-tooltip" title="Riscado"></button>
                    </span>
                    <span class="ql-formats">
                        <!-- Add subscript and superscript buttons -->
                        <button class="ql-script bs-tooltip" title="Subscrito" value="sub"></button>
                        <button class="ql-script bs-tooltip" title="Sobrescrito" value="super"></button>
                    </span>
                    <span class="ql-formats">
                        <button class="ql-list bs-tooltip" value="ordered" title="Lista ordenada"></button>
                        <button class="ql-list bs-tooltip" value="bullet" title="Lista de marcadores"></button>
                        <select class="ql-align bs-tooltip" title="Alinhar"></select>
                    </span>
                    <span class="ql-formats">
                        <button v-if="allowLinks" class="ql-link bs-tooltip" title="Adicionar link"></button>
                        <button v-if="allowImages" class="ql-image bs-tooltip" title="Adicionar imagem"></button>
                    </span>
                </div>
                <div v-el:holder>
                    <div tabindex="1" v-el:editor :style="{'height': height+'px', 'min-height': height+'px'}">
            
                    </div>
                </div>
            </div>
            <div v-bind:data-field="fieldName"></div>
        </div>
    `
})



// WEBPACK FOOTER //
// ./resources/assets/javascript/sci/components/form/editor.js